import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FaCalculator, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';

// ============================================================
// CONSTANTES
// ============================================================
const ACTIVITY_FACTORS = {
  sedentario: { label: 'Sedentario', factor: 1.2, desc: 'Poco o ningún ejercicio' },
  ligero: { label: 'Ligera', factor: 1.375, desc: 'Ejercicio 1-3 días/semana' },
  moderado: { label: 'Moderada', factor: 1.55, desc: 'Ejercicio 3-5 días/semana' },
  alto: { label: 'Alta', factor: 1.725, desc: 'Ejercicio 6-7 días/semana' },
  muy_alto: { label: 'Muy Alta', factor: 1.9, desc: 'Ejercicio intenso o trabajo físico' }
};

const GOAL_ADJUSTMENTS = {
  perdida_rapida: { label: 'Pérdida rápida', pct: -25, color: '#EF4444' },
  perdida_moderada: { label: 'Pérdida moderada', pct: -20, color: '#F97316' },
  perdida_lenta: { label: 'Pérdida lenta', pct: -10, color: '#F59E0B' },
  mantenimiento: { label: 'Mantenimiento', pct: 0, color: '#22C55E' },
  ganancia_lenta: { label: 'Ganancia lenta', pct: 10, color: '#3B82F6' },
  ganancia_moderada: { label: 'Ganancia moderada', pct: 15, color: '#6366F1' },
  ganancia_rapida: { label: 'Ganancia rápida', pct: 20, color: '#8B5CF6' }
};

const MACRO_PREFERENCES = {
  balanceado: { label: 'Balanceado', protein: 30, carbs: 40, fat: 30 },
  alto_carbos: { label: 'Alto en Carbos', protein: 25, carbs: 55, fat: 20 },
  bajo_carbos: { label: 'Bajo en Carbos', protein: 30, carbs: 25, fat: 45 },
  alto_proteina: { label: 'Muy Alto en Proteína', protein: 40, carbs: 35, fat: 25 }
};

const MacroCalculator = ({ clientData, onMacrosCalculated, onClientDataChange }) => {
  const [calcData, setCalcData] = useState({
    knows_body_fat: false,
    sex: 'mujer',
    weight: clientData?.weight || '',
    height: clientData?.height || '',
    age: clientData?.age || '',
    body_fat_pct: '',
    activity_level: clientData?.activity_level || 'moderado',
    goal: 'mantenimiento',
    macro_preference: 'balanceado',
    custom_macros: false,
    custom_protein_pct: 30,
    custom_carbs_pct: 40,
    custom_fat_pct: 30,
    num_meals: clientData?.num_meals || 5
  });
  
  const [showDetails, setShowDetails] = useState(false);

  // Sync with external clientData
  useEffect(() => {
    if (clientData) {
      setCalcData(prev => ({
        ...prev,
        weight: clientData.weight || prev.weight,
        height: clientData.height || prev.height,
        age: clientData.age || prev.age,
        activity_level: clientData.activity_level || prev.activity_level,
        num_meals: clientData.num_meals || prev.num_meals
      }));
    }
  }, [clientData?.weight, clientData?.height, clientData?.age, clientData?.activity_level, clientData?.num_meals]);

  const updateField = useCallback((field, value) => {
    setCalcData(prev => ({ ...prev, [field]: value }));
  }, []);

  // ============================================================
  // CÁLCULOS
  // ============================================================
  const calculations = useMemo(() => {
    const w = parseFloat(calcData.weight) || 0;
    const h = parseFloat(calcData.height) || 0;
    const a = parseInt(calcData.age) || 25;
    const bf = parseFloat(calcData.body_fat_pct) || 0;
    
    if (w <= 0) return null;
    
    let tmb = 0;
    let method = '';
    
    // 2026-02 · Si el campo de % grasa corporal tiene cualquier valor > 0
    // (ingresado por la usuaria o cargado por el admin), SIEMPRE usamos
    // Katch-McArdle. Sólo si está vacío o es 0 caemos a Mifflin-St Jeor.
    if (bf > 0) {
      // Katch-McArdle
      const leanMass = w * (1 - bf / 100);
      tmb = 370 + (21.6 * leanMass);
      method = 'Katch-McArdle';
    } else {
      // Mifflin-St Jeor (respaldo cuando no hay % grasa disponible)
      if (h <= 0 || a <= 0) return null;
      if (calcData.sex === 'hombre') {
        tmb = (10 * w) + (6.25 * h) - (5 * a) + 5;
      } else {
        tmb = (10 * w) + (6.25 * h) - (5 * a) - 161;
      }
      method = 'Mifflin-St Jeor';
    }
    
    // Factor de actividad
    const actFactor = ACTIVITY_FACTORS[calcData.activity_level]?.factor || 1.55;
    const maintenance = Math.round(tmb * actFactor);
    
    // Ajuste por objetivo
    const goalAdj = GOAL_ADJUSTMENTS[calcData.goal]?.pct || 0;
    const targetKcal = Math.round(maintenance * (1 + goalAdj / 100));
    
    // Macros
    let pPct, cPct, fPct;
    if (calcData.custom_macros) {
      pPct = calcData.custom_protein_pct;
      cPct = calcData.custom_carbs_pct;
      fPct = calcData.custom_fat_pct;
    } else {
      const pref = MACRO_PREFERENCES[calcData.macro_preference] || MACRO_PREFERENCES.balanceado;
      pPct = pref.protein;
      cPct = pref.carbs;
      fPct = pref.fat;
    }
    
    const proteinG = Math.round((targetKcal * pPct / 100) / 4);
    const carbsG = Math.round((targetKcal * cPct / 100) / 4);
    const fatG = Math.round((targetKcal * fPct / 100) / 9);
    
    // Per meal
    const numMeals = parseInt(calcData.num_meals) || 5;
    const proteinPerMeal = Math.round(proteinG / numMeals);
    const carbsPerMeal = Math.round(carbsG / numMeals);
    const fatPerMeal = Math.round(fatG / numMeals);
    const kcalPerMeal = Math.round(targetKcal / numMeals);
    
    return {
      method,
      tmb: Math.round(tmb),
      maintenance,
      goalAdjustment: goalAdj,
      targetKcal,
      proteinG, carbsG, fatG,
      pPct, cPct, fPct,
      numMeals,
      proteinPerMeal, carbsPerMeal, fatPerMeal, kcalPerMeal
    };
  }, [calcData]);

  // Auto-notify parent when calculations change
  useEffect(() => {
    if (calculations && onMacrosCalculated) {
      onMacrosCalculated({
        target_calories: calculations.targetKcal,
        target_protein: calculations.proteinG,
        target_carbs: calculations.carbsG,
        target_fat: calculations.fatG,
        num_meals: calculations.numMeals,
        protein_per_meal: calculations.proteinPerMeal,
        carbs_per_meal: calculations.carbsPerMeal,
        fat_per_meal: calculations.fatPerMeal,
        kcal_per_meal: calculations.kcalPerMeal
      });
    }
  }, [calculations, onMacrosCalculated]);

  const inputCls = "bg-gray-800 border-gray-600 text-white h-9 text-sm";
  const labelCls = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="space-y-4" data-testid="macro-calculator">
      {/* Datos del usuario */}
      <div className="p-4 rounded-lg border" style={{ backgroundColor: 'rgba(16,185,129,0.08)', borderColor: 'rgba(16,185,129,0.3)' }}>
        <h3 className="text-sm font-bold text-emerald-400 mb-3 flex items-center gap-2">
          <FaCalculator /> CALCULADOR DE MACROS
        </h3>
        
        {/* Conoce % grasa? */}
        <div className="flex items-center gap-4 mb-4">
          <span className="text-xs text-gray-400">¿Conoce su % de grasa corporal?</span>
          <div className="flex gap-2">
            <Button size="sm" variant={calcData.knows_body_fat ? "default" : "outline"}
              className={`h-7 text-xs ${calcData.knows_body_fat ? 'bg-emerald-600' : 'border-gray-600 text-gray-400'}`}
              onClick={() => updateField('knows_body_fat', true)}>Sí</Button>
            <Button size="sm" variant={!calcData.knows_body_fat ? "default" : "outline"}
              className={`h-7 text-xs ${!calcData.knows_body_fat ? 'bg-emerald-600' : 'border-gray-600 text-gray-400'}`}
              onClick={() => updateField('knows_body_fat', false)}>No</Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {!calcData.knows_body_fat && (
            <div>
              <label className={labelCls}>Sexo</label>
              <Select value={calcData.sex} onValueChange={(v) => updateField('sex', v)}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mujer">Mujer</SelectItem>
                  <SelectItem value="hombre">Hombre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div>
            <label className={labelCls}>Peso (kg)</label>
            <Input type="number" className={inputCls} value={calcData.weight}
              onChange={(e) => updateField('weight', e.target.value)} placeholder="60" />
          </div>
          
          {!calcData.knows_body_fat && (
            <>
              <div>
                <label className={labelCls}>Altura (cm)</label>
                <Input type="number" className={inputCls} value={calcData.height}
                  onChange={(e) => updateField('height', e.target.value)} placeholder="165" />
              </div>
              <div>
                <label className={labelCls}>Edad</label>
                <Input type="number" className={inputCls} value={calcData.age}
                  onChange={(e) => updateField('age', e.target.value)} placeholder="30" />
              </div>
            </>
          )}
          
          {calcData.knows_body_fat && (
            <div>
              <label className={labelCls}>% Grasa</label>
              <Input type="number" className={inputCls} value={calcData.body_fat_pct}
                onChange={(e) => updateField('body_fat_pct', e.target.value)} placeholder="25" />
            </div>
          )}
          
          <div>
            <label className={labelCls}>Actividad</label>
            <Select value={calcData.activity_level} onValueChange={(v) => updateField('activity_level', v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ACTIVITY_FACTORS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className={labelCls}>Nº Comidas</label>
            <Select value={calcData.num_meals.toString()} onValueChange={(v) => {
              updateField('num_meals', parseInt(v));
              if (onClientDataChange) onClientDataChange({ num_meals: parseInt(v) });
            }}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3,4,5,6,7].map(n => <SelectItem key={n} value={n.toString()}>{n} comidas</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Objetivo y Preferencia */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className={labelCls}>Objetivo</label>
            <Select value={calcData.goal} onValueChange={(v) => updateField('goal', v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(GOAL_ADJUSTMENTS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label} ({v.pct > 0 ? '+' : ''}{v.pct}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className={labelCls}>Preferencia de Macros</label>
            <Select value={calcData.macro_preference} onValueChange={(v) => {
              updateField('macro_preference', v);
              updateField('custom_macros', false);
            }}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MACRO_PREFERENCES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v.label} ({v.protein}P / {v.carbs}C / {v.fat}G)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Custom macro percentages (editable by admin) */}
        <div className="mt-2">
          <button className="text-xs text-emerald-400 hover:text-emerald-300"
            onClick={() => {
              const pref = MACRO_PREFERENCES[calcData.macro_preference] || MACRO_PREFERENCES.balanceado;
              setCalcData(prev => ({
                ...prev,
                custom_macros: !prev.custom_macros,
                custom_protein_pct: pref.protein,
                custom_carbs_pct: pref.carbs,
                custom_fat_pct: pref.fat
              }));
            }}>
            {calcData.custom_macros ? '- Usar preset' : '+ Personalizar %'}
          </button>
          
          {calcData.custom_macros && (
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div>
                <label className={labelCls}>% Proteína</label>
                <Input type="number" className={inputCls} value={calcData.custom_protein_pct}
                  onChange={(e) => updateField('custom_protein_pct', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>% Carbos</label>
                <Input type="number" className={inputCls} value={calcData.custom_carbs_pct}
                  onChange={(e) => updateField('custom_carbs_pct', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>% Grasas</label>
                <Input type="number" className={inputCls} value={calcData.custom_fat_pct}
                  onChange={(e) => updateField('custom_fat_pct', parseInt(e.target.value) || 0)} />
              </div>
              {(calcData.custom_protein_pct + calcData.custom_carbs_pct + calcData.custom_fat_pct) !== 100 && (
                <p className="col-span-3 text-xs text-red-400">
                  Total: {calcData.custom_protein_pct + calcData.custom_carbs_pct + calcData.custom_fat_pct}% (debe ser 100%)
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resultado del cálculo */}
      {calculations && (
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'rgba(59,130,246,0.08)', borderColor: 'rgba(59,130,246,0.3)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-blue-400">RESULTADO DIARIO</h3>
            <button className="text-xs text-gray-500 flex items-center gap-1" onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? <FaChevronUp /> : <FaChevronDown />} Detalles
            </button>
          </div>
          
          {showDetails && (
            <div className="text-xs text-gray-500 mb-3 space-y-1 p-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
              <p>Método: {calculations.method}</p>
              <p>TMB: {calculations.tmb} kcal</p>
              <p>Mantenimiento: {calculations.maintenance} kcal</p>
              <p>Ajuste objetivo: {calculations.goalAdjustment > 0 ? '+' : ''}{calculations.goalAdjustment}%</p>
              <p>Distribución: {calculations.pPct}%P / {calculations.cPct}%C / {calculations.fPct}%G</p>
            </div>
          )}
          
          {/* Daily totals */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
              <p className="text-lg font-bold text-white">{calculations.targetKcal}</p>
              <p className="text-xs text-gray-400">kcal/día</p>
            </div>
            <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(59,130,246,0.15)' }}>
              <p className="text-lg font-bold text-blue-400">{calculations.proteinG}g</p>
              <p className="text-xs text-gray-400">Proteína</p>
            </div>
            <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}>
              <p className="text-lg font-bold text-amber-400">{calculations.carbsG}g</p>
              <p className="text-xs text-gray-400">Carbos</p>
            </div>
            <div className="text-center p-2 rounded" style={{ backgroundColor: 'rgba(168,85,247,0.15)' }}>
              <p className="text-lg font-bold text-purple-400">{calculations.fatG}g</p>
              <p className="text-xs text-gray-400">Grasas</p>
            </div>
          </div>
          
          {/* Per meal */}
          <div className="p-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <p className="text-xs text-gray-400 mb-1">Por comida ({calculations.numMeals} comidas):</p>
            <div className="flex gap-4 text-xs">
              <span className="text-white font-medium">{calculations.kcalPerMeal} kcal</span>
              <span className="text-blue-400">{calculations.proteinPerMeal}g P</span>
              <span className="text-amber-400">{calculations.carbsPerMeal}g C</span>
              <span className="text-purple-400">{calculations.fatPerMeal}g G</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MacroCalculator;

// ============================================================
// SMART MEAL GENERATION FUNCTIONS (exportadas para uso en OfflineGenerator)
// ============================================================

/**
 * Genera una comida con cálculo inteligente de gramos
 * Sigue el orden: Proteína → Fruta (opcional) → Carbohidrato → Grasa inteligente → Vegetal
 */
export const generateSmartMeal = ({
  mealIndex,
  totalMeals,
  dailyMacros, // { target_protein, target_carbs, target_fat, target_calories }
  foodDatabase,
  postWorkoutMeal, // número de comida post-entreno ('none' o '1','2',etc)
  fruitMeals // array de indices de comidas que llevan fruta (máx 2: desayuno + post)
}) => {
  const numMeals = totalMeals || 5;
  
  // Macros por comida (división equitativa)
  const proteinTarget = Math.round((dailyMacros.target_protein || 0) / numMeals);
  const carbsTarget = Math.round((dailyMacros.target_carbs || 0) / numMeals);
  const fatTarget = Math.round((dailyMacros.target_fat || 0) / numMeals);
  const kcalTarget = Math.round((dailyMacros.target_calories || 0) / numMeals);
  
  const mealNames = {
    3: ['Desayuno', 'Almuerzo', 'Cena'],
    4: ['Desayuno', 'Almuerzo', 'Snack PM', 'Cena'],
    5: ['Desayuno', 'Snack AM', 'Almuerzo', 'Snack PM', 'Cena'],
    6: ['Desayuno', 'Snack AM', 'Almuerzo', 'Snack PM', 'Cena', 'Post-Entreno'],
    7: ['Desayuno', 'Snack AM', 'Almuerzo', 'Snack PM', 'Cena', 'Snack PM', 'Post-Entreno']
  };
  const mealTimes = {
    3: ['07:00', '13:00', '20:00'],
    4: ['07:00', '13:00', '17:00', '20:00'],
    5: ['07:00', '10:00', '13:00', '17:00', '20:00'],
    6: ['07:00', '10:00', '13:00', '17:00', '20:00', '22:00'],
    7: ['06:30', '09:00', '12:00', '15:00', '17:30', '20:00', '22:00']
  };
  
  const names = mealNames[numMeals] || mealNames[5];
  const times = mealTimes[numMeals] || mealTimes[5];
  
  const isPostWorkout = postWorkoutMeal && postWorkoutMeal !== 'none' && parseInt(postWorkoutMeal) === (mealIndex + 1);
  const hasFruit = fruitMeals && fruitMeals.includes(mealIndex);
  
  const items = [];
  let fatUsedFromProtein = 0;
  let carbsUsedFromFruit = 0;
  
  const getRandomFood = (foods) => {
    if (!foods || foods.length === 0) return null;
    return foods[Math.floor(Math.random() * foods.length)];
  };
  
  // Redondeo visual
  const roundVisual = (g, type) => {
    if (type === 'fat') return Math.round(g);
    return Math.round(g / 5) * 5 || 5; // múltiplos de 5, mínimo 5
  };
  
  // ---- E1) PROTEÍNA (OBLIGATORIO) ----
  const proteinFoods = foodDatabase.proteinas || [];
  if (proteinFoods.length > 0) {
    const proteinFood = getRandomFood(proteinFoods);
    if (proteinFood) {
      const protPer100 = proteinFood.protein || 25;
      const fatPer100 = proteinFood.fat || 0;
      
      // Gramos exactos para cubrir meta de proteína
      const exactGrams = (proteinTarget / protPer100) * 100;
      const gramsRounded = roundVisual(exactGrams, 'protein');
      
      // Grasa que aporta esta proteína
      fatUsedFromProtein = (gramsRounded * fatPer100) / 100;
      
      items.push({
        category: 'proteinas',
        food_name: proteinFood.name,
        quantity: gramsRounded,
        unit: 'g',
        calories: proteinFood.calories || 0,
        protein: proteinFood.protein || 0,
        carbs: proteinFood.carbs || 0,
        fat: proteinFood.fat || 0,
        grams: gramsRounded,
        measurement_note: 'crudo'
      });
    }
  }
  
  // ---- E2) FRUTA (OPCIONAL, CUENTA CARBOS) ----
  if (hasFruit) {
    const fruitFoods = foodDatabase.frutas || [];
    if (fruitFoods.length > 0) {
      const fruitFood = getRandomFood(fruitFoods);
      if (fruitFood) {
        // Porción estándar de fruta: 100-150g
        const fruitGrams = 120;
        const carbsPer100 = fruitFood.carbs || 14;
        carbsUsedFromFruit = (fruitGrams * carbsPer100) / 100;
        
        items.push({
          category: 'frutas',
          food_name: fruitFood.name,
          quantity: fruitGrams,
          unit: 'g',
          calories: fruitFood.calories || 0,
          protein: fruitFood.protein || 0,
          carbs: fruitFood.carbs || 0,
          fat: fruitFood.fat || 0,
          grams: fruitGrams,
          measurement_note: 'crudo'
        });
      }
    }
  }
  
  // ---- E3) CARBOHIDRATO PRINCIPAL (OBLIGATORIO) ----
  const carbFoods = foodDatabase.carbohidratos || [];
  const carbsRemaining = Math.max(0, carbsTarget - carbsUsedFromFruit);
  
  if (carbFoods.length > 0 && carbsRemaining > 5) {
    const carbFood = getRandomFood(carbFoods);
    if (carbFood) {
      const carbsPer100 = carbFood.carbs || 30;
      const exactGrams = (carbsRemaining / carbsPer100) * 100;
      const gramsRounded = roundVisual(exactGrams, 'carbs');
      
      // Determinar tipo de medición
      const name = (carbFood.name || '').toLowerCase();
      let note = 'cocido';
      if (name.includes('avena') || name.includes('crema de arroz') || name.includes('hojuelas') || name.includes('cereal')) {
        note = 'crudo (polvo)';
      } else if (name.includes('pan') || name.includes('arepa')) {
        note = 'listo para consumir';
      }
      
      items.push({
        category: 'carbohidratos',
        food_name: carbFood.name,
        quantity: gramsRounded,
        unit: 'g',
        calories: carbFood.calories || 0,
        protein: carbFood.protein || 0,
        carbs: carbFood.carbs || 0,
        fat: carbFood.fat || 0,
        grams: gramsRounded,
        measurement_note: note
      });
    }
  }
  
  // ---- F) GRASA INTELIGENTE ----
  const fatRemaining = fatTarget - fatUsedFromProtein;
  
  if (isPostWorkout) {
    // Comida post-entreno: SIN GRASAS
    items.push({
      category: 'grasas',
      food_name: 'No aplica (Post-Entreno)',
      quantity: 0,
      unit: 'g',
      calories: 0, protein: 0, carbs: 0, fat: 0,
      grams: 0,
      skip: true,
      measurement_note: 'Post-entreno: sin grasas'
    });
  } else if (fatRemaining <= 0) {
    // La proteína ya cubre la grasa
    items.push({
      category: 'grasas',
      food_name: 'No necesitas grasa adicional',
      quantity: 0,
      unit: 'g',
      calories: 0, protein: 0, carbs: 0, fat: 0,
      grams: 0,
      skip: true,
      measurement_note: 'La proteína cubre la grasa de esta comida'
    });
  } else {
    // Agregar grasa saludable
    const fatFoods = foodDatabase.grasas || [];
    if (fatFoods.length > 0) {
      const fatFood = getRandomFood(fatFoods);
      if (fatFood) {
        const fatPer100 = fatFood.fat || 100;
        const exactGrams = (fatRemaining / fatPer100) * 100;
        const gramsRounded = Math.round(exactGrams); // grasas: redondeo a 1g
        
        items.push({
          category: 'grasas',
          food_name: fatFood.name,
          quantity: Math.max(1, gramsRounded),
          unit: 'g',
          calories: fatFood.calories || 0,
          protein: fatFood.protein || 0,
          carbs: fatFood.carbs || 0,
          fat: fatFood.fat || 0,
          grams: Math.max(1, gramsRounded),
          measurement_note: 'gramos'
        });
      }
    }
  }
  
  // ---- G) VEGETALES (LIBRE) ----
  const vegFoods = foodDatabase.vegetales || [];
  if (vegFoods.length > 0) {
    const vegFood = getRandomFood(vegFoods);
    if (vegFood) {
      items.push({
        category: 'vegetales',
        food_name: vegFood.name,
        quantity: 150,
        unit: 'g',
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        grams: 150,
        skip: true, // No afectan macros
        measurement_note: 'Sugerido 100-200g (libre)'
      });
    }
  }
  
  // Calcular macros totales de la comida
  let totalP = 0, totalC = 0, totalF = 0, totalCal = 0;
  items.forEach(item => {
    if (!item.skip) {
      const mult = (item.grams || 0) / 100;
      totalP += (item.protein || 0) * mult;
      totalC += (item.carbs || 0) * mult;
      totalF += (item.fat || 0) * mult;
      totalCal += (item.calories || 0) * mult;
    }
  });
  
  return {
    meal_name: names[mealIndex] || `Comida ${mealIndex + 1}`,
    meal_time: times[mealIndex] || '',
    is_post_workout: isPostWorkout,
    has_fruit: hasFruit,
    target_protein: proteinTarget,
    target_carbs: carbsTarget,
    target_fat: fatTarget,
    target_calories: kcalTarget,
    actual_protein: Math.round(totalP),
    actual_carbs: Math.round(totalC),
    actual_fat: Math.round(totalF),
    actual_calories: Math.round(totalCal),
    items,
    options: [] // Se llenan con 3 opciones diferentes
  };
};
